import { defineStore } from "pinia";

import { LocationsState, Hit, Filters, SearchPlace } from "../interfaces/locations";
import { Filter, Facet, FacetCount, Result } from "../interfaces/facets";
import { QueryParams } from "../interfaces/query-parameters";

import { useSearchParameters, useTypesenseClient } from "../composables/typesense";
import { useQueryArrayUrlify, useRemoveQueryUrlify, useQueryUrlify } from "../composables/query-parameters";
import { DEFAULT_LOCATION_RADIUS_KM } from "../composables/mapbox-geocoding";
import {
	LOCATION_AREA_MIN,
	LOCATION_AREA_MAX,
	documentMatchesAreaFilter,
	isLocationAreaFilterActive,
	normalizeLocationAreaRange,
} from "../composables/location-area";

export const useLocationsStore = defineStore("locations", {
	state: (): LocationsState => ({
		loading: false,
		facets: [],
		filters: {
			searchQuery: "",
			filterQuery: "",
			area: {
				min: LOCATION_AREA_MIN,
				max: LOCATION_AREA_MAX,
			},
			countries: {
				field: "country",
				values: [],
				combination: "&&",
			},
			buildingParts: {
				field: "buildingParts",
				values: [],
				combination: "&&",
			},
			availableNow: {
				field: "availableNow",
				value: true,
				combination: "&&",
			},
			availableSoon: {
				field: "availableSoon",
				value: true,
				combination: "&&",
			},
			project: {
				field: null,
				value: true,
				combination: null,
			},
			building: {
				field: null,
				value: true,
				combination: null,
			},
			type: {
				field: "type",
				values: ["project", "building"],
				combination: "&&",
				includeInUrl: false,
			},
		},
		pagination: {
			current: 0,
			totalPages: null,
			totalResults: null,
			foundResults: null,
			itemsPerPage: 250,
		},
		sorting: [
			{
				type: "title",
				order: "asc",
			},
		],
		sortMode: "alphabetical",
		index: import.meta.env.VITE_LOCATIONS_INDEX ?? "wdp_production_locations",
		locations: null,
		view: "map",
		searchPlace: null,
		radiusKm: null,
	}),
	actions: {
		async fetch(reset = false, queryParams?: Array<QueryParams>) {
			this.loading = true;

			// set URL query parameters if existing
			if (queryParams) {
				this.setQueryFilters(queryParams);
			}
			// else {
			//     this.setFiltersQuery()
			// }

			const client = useTypesenseClient();
			const searchParameters = useSearchParameters(this.filters, this.sorting);

			if (!reset && this.pagination.current) {
				this.pagination.current += 1;
			} else {
				this.pagination.current = 1;
				this.pagination.totalPages = 0;
				this.pagination.totalResults = null;
			}

			const searchRequests = {
			searches: [
					{
						highlight_full_fields: "title,country,availableNow,availableSoon",
						collection: this.index,
						facet_by: "country,availableNow,availableSoon",
						page: this.pagination.current,
						per_page: this.pagination.itemsPerPage,
						...searchParameters,
					},
					{
						highlight_full_fields: "title,country,availableNow,availableSoon",
						collection: this.index,
						facet_by: "country,availableNow,availableSoon",
						q: "*",
						page: 1,
						per_page: 1,
						filter_by: `site:${window.language}`,
					},
				],
			};

			await client.multiSearch.perform(searchRequests).then((results: any) => {
				const filteredResults = results.results[0];
				const facets = results.results[1].facet_counts;

				this.facets = facets;
				this.pagination.current = filteredResults.page;
				this.pagination.totalResults = filteredResults.out_of;

				if (filteredResults.hits) {
					let hits: Array<Hit> = filteredResults.hits;
					const areaFilterActive = isLocationAreaFilterActive(
						this.filters.area.min,
						this.filters.area.max,
					);

					if (areaFilterActive) {
						hits = hits.filter((hit) =>
							documentMatchesAreaFilter(
								hit.document,
								this.filters.area.min,
								this.filters.area.max,
							),
						);
					}

					this.pagination.foundResults = areaFilterActive ? hits.length : filteredResults.found;
					this.pagination.totalPages = this.pagination.itemsPerPage
						? Math.ceil(this.pagination.foundResults / this.pagination.itemsPerPage)
						: 0;

					if (reset) {
						this.locations = hits;
					} else {
						this.locations = [...(this.locations || []), ...hits];
					}
				} else {
					this.pagination.foundResults = filteredResults.found;
					this.pagination.totalPages = this.pagination.itemsPerPage
						? Math.ceil(filteredResults.found / this.pagination.itemsPerPage)
						: 0;
				}

				this.loading = false;
			});
		},
		clearFilters() {
			this.filters.area.min = LOCATION_AREA_MIN;
			this.filters.area.max = LOCATION_AREA_MAX;

			this.filters.countries.values = [];
			this.filters.buildingParts.values = [];
			this.filters.availableNow.value = true;
			this.filters.availableSoon.value = true;
			this.filters.project.value = true;
			this.filters.building.value = true;
			this.filters.type.values = ["building", "project"];
			this.searchPlace = null;
			this.radiusKm = null;

			const url = new URL(window.location.href);
			[
				"countries",
				"availableNow",
				"availableSoon",
				"project",
				"building",
				"place",
				"lat",
				"lng",
				"radius",
				"areaMin",
				"areaMax",
				"buildingParts",
			].forEach((key) => {
				url.searchParams.delete(key);
			});
			window.history.replaceState({}, "", url.toString());

			this.setFiltersQuery();
			this.fetch(true);
		},
		setCountryFilter(value: string | null) {
			for (const slug of [...this.filters.countries.values]) {
				useRemoveQueryUrlify("countries", slug);
			}

			this.filters.countries.values = value ? [value] : [];

			if (value) {
				useQueryArrayUrlify("countries", value);
			} else {
				useRemoveQueryUrlify("countries");
			}

			this.setFiltersQuery();
			this.fetch(true);
		},
		setSearchPlace(place: SearchPlace | null) {
			this.searchPlace = place;

			if (place) {
				if (!this.radiusKm) {
					this.radiusKm = DEFAULT_LOCATION_RADIUS_KM;
				}

				useQueryUrlify("place", place.label);
				useQueryUrlify("lat", String(place.latitude));
				useQueryUrlify("lng", String(place.longitude));
				useQueryUrlify("radius", String(this.radiusKm));
			} else {
				this.radiusKm = null;
				useRemoveQueryUrlify("place");
				useRemoveQueryUrlify("lat");
				useRemoveQueryUrlify("lng");
				useRemoveQueryUrlify("radius");
			}

			this.setFiltersQuery();
			this.fetch(true);
		},
		setRadius(km: number) {
			this.radiusKm = km;

			if (this.searchPlace) {
				useQueryUrlify("radius", String(km));
				this.setFiltersQuery();
				this.fetch(true);
			}
		},
		setAreaRange(min: number, max: number) {
			const { min: nextMin, max: nextMax } = normalizeLocationAreaRange(min, max);

			this.filters.area.min = nextMin;
			this.filters.area.max = nextMax;

			if (isLocationAreaFilterActive(nextMin, nextMax)) {
				if (nextMin > LOCATION_AREA_MIN) {
					useQueryUrlify("areaMin", String(nextMin));
				} else {
					useRemoveQueryUrlify("areaMin");
				}

				if (nextMax < LOCATION_AREA_MAX) {
					useQueryUrlify("areaMax", String(nextMax));
				} else {
					useRemoveQueryUrlify("areaMax");
				}
			} else {
				useRemoveQueryUrlify("areaMin");
				useRemoveQueryUrlify("areaMax");
			}

			this.setFiltersQuery();
			this.fetch(true);
		},
		applyAreaPreset(presetMin: number) {
			this.setAreaRange(presetMin, LOCATION_AREA_MAX);
		},
		setFilter(type: keyof Filters, value: string) {
			const settedFilter: Filter | string = this.filters[type] ?? null;

			if (typeof settedFilter !== "string" && (settedFilter?.includeInUrl ? settedFilter?.includeInUrl : true)) {
				if (settedFilter.hasOwnProperty("values") && type !== "type") {
					if (settedFilter.values.indexOf(value) < 0) {
						settedFilter.values.push(value);
						useQueryArrayUrlify(type, value);
					} else {
						settedFilter.values = settedFilter.values.filter((item: string) => item !== value);
						useRemoveQueryUrlify(type, value);
					}
				}

				if (settedFilter.hasOwnProperty("value")) {
					settedFilter.value = value;

					// if (type !== 'availableSoon') {
					// useRemoveQueryUrlify(type)
					// } else {
					//     useQueryArrayUrlify('availableSoon', this.filters.availableSoon.value)
					// }

					// if (value !== null && value !== '' && value !== false) {
					useQueryUrlify(type, value);
					// }
				}

				if (type == "type") {
					if (settedFilter.values.indexOf(value) < 0) {
						settedFilter.values.push(value);
					} else {
						settedFilter.values = settedFilter.values.filter((item: string) => item !== value);
					}

					this.filters.building.value = settedFilter.values.find((item: string) => item === "building")
						? true
						: false;
					this.filters.project.value = settedFilter.values.find((item: string) => item === "project")
						? true
						: false;

					this.setProjectBuilding();

					// useRemoveQueryUrlify('building')
					useQueryUrlify("building", this.filters.building.value);

					// useRemoveQueryUrlify('project')
					useQueryUrlify("project", this.filters.project.value);
				}
			}

			this.setFiltersQuery();
			this.fetch(true);
		},

		setFiltersQuery() {
			this.filters.filterQuery = "";
			const language = window.language;

			if (this.searchPlace && this.radiusKm) {
				this.filters.filterQuery += `location:(${this.searchPlace.latitude}, ${this.searchPlace.longitude}, ${this.radiusKm} km) && `;
			}

			// Area is filtered client-side so totals match card display (units/building parts).

			if (this.filters.countries.values.length > 0) {
				this.filters.filterQuery +=
					this.filters.countries.field +
					":[" +
					this.filters.countries.values.toString() +
					"] " +
					this.filters.countries.combination +
					" ";
			}

			this.filters.filterQuery +=
				this.filters.type.field +
				":[" +
				this.filters.type.values.toString() +
				"] " +
				this.filters.type.combination +
				" ";

			// availability checks will always be here, they are merged within ()
			if (this.filters.availableNow.value === true && this.filters.availableSoon.value === false) {
				// Check if the filter is availableNow is true - but not availableSoon - make a query with availableNow only
				this.filters.filterQuery +=
					"(" +
					this.filters.availableNow.field +
					":[" +
					this.filters.availableNow.value +
					"])" +
					this.filters.availableNow.combination +
					" ";
			} else if (this.filters.availableNow.value === false && this.filters.availableSoon.value === true) {
				// Check if the filter is availableSoon is true - but not availableNow - make a query with availableSoon only
				this.filters.filterQuery +=
					"(" +
					this.filters.availableSoon.field +
					":[" +
					this.filters.availableSoon.value +
					"]) " +
					this.filters.availableSoon.combination +
					" ";
			} else if (this.filters.availableNow.value === false && this.filters.availableSoon.value === false) {
				// Check if the filter availableNow and availableSoon is false - make a query where both values are false - so it doesn't display anything.
				this.filters.filterQuery +=
					"(" +
					this.filters.availableNow.field +
					":[false] && " +
					this.filters.availableSoon.field +
					":[false])" +
					this.filters.availableSoon.combination +
					" ";
			} else {
				// Check if the filter availableNow and availableSoon is both true - make a query where both values are true - so it displays everything.
				this.filters.filterQuery +=
					"(" + this.filters.availableNow.field + ":[" + this.filters.availableNow.value + "] || ";
				this.filters.filterQuery +=
					this.filters.availableSoon.field +
					":[" +
					this.filters.availableSoon.value +
					"]) " +
					this.filters.availableSoon.combination +
					" ";
			}

			this.filters.filterQuery += `site:${language} `;
		},

		setQueryFilters(queryParams: Array<QueryParams>) {
			const placeParam = queryParams.find((param) => param.type === "place");
			const latParam = queryParams.find((param) => param.type === "lat");
			const lngParam = queryParams.find((param) => param.type === "lng");
			const radiusParam = queryParams.find((param) => param.type === "radius");
			const areaMinParam = queryParams.find((param) => param.type === "areaMin");
			const areaMaxParam = queryParams.find((param) => param.type === "areaMax");

			if (areaMinParam?.value || areaMaxParam?.value) {
				const parsedMin = areaMinParam?.value
					? parseInt(areaMinParam.value, 10)
					: LOCATION_AREA_MIN;
				const parsedMax = areaMaxParam?.value
					? parseInt(areaMaxParam.value, 10)
					: LOCATION_AREA_MAX;

				if (!Number.isNaN(parsedMin) && !Number.isNaN(parsedMax)) {
					const { min, max } = normalizeLocationAreaRange(parsedMin, parsedMax);
					this.filters.area.min = min;
					this.filters.area.max = max;
				}
			}

			if (latParam?.value && lngParam?.value) {
				const latitude = parseFloat(latParam.value);
				const longitude = parseFloat(lngParam.value);

				if (!Number.isNaN(latitude) && !Number.isNaN(longitude)) {
					this.searchPlace = {
						label: placeParam?.value ?? "",
						latitude,
						longitude,
					};
					this.radiusKm = radiusParam?.value
						? parseInt(radiusParam.value, 10)
						: DEFAULT_LOCATION_RADIUS_KM;
				}
			}

			for (const key in queryParams as QueryParams[]) {
				const values = queryParams[key].values;
				const type: string = queryParams[key].type;

				if (type === "view") {
					const view = values[0]?.name ? values[0]?.name : "map";
					this.toggleView(view);
				}
				if (type === "sortBy") {
					const mode = values[0]?.name === "recent" ? "recent" : "alphabetical";
					this.applySorting(mode);
				}

				if (values) {
					const filter = this.filters[type as keyof Filters];

					if (filter) {
						// handle available now / soon if one is set in the query
						if (filter.field === "availableNow") {
							const soon = queryParams.find((param) => param.type === "availableSoon");

							if (!soon) {
								useQueryUrlify("availableSoon", this.filters.availableSoon.value);
							}
						}

						if (filter.field === "availableSoon") {
							const now = queryParams.find((param) => param.type === "availableNow");

							if (!now) {
								useQueryUrlify("availableNow", this.filters.availableNow.value);
							}
						}

						// handle project / building if one is set in the query
						if (type === "building") {
							this.filters.building.value = values[0].name === "true";
						}

						if (type === "project") {
							this.filters.project.value = values[0].name === "true";
						}

						this.setProjectBuilding();

						if (filter.hasOwnProperty("values")) {
							this.filters[type as keyof Filters].values = values.map(
								(item: { name: string }) => item.name,
							);
						}

						if (filter.hasOwnProperty("value")) {
							if (typeof this.filters[type as keyof Filters].value === "boolean") {
								this.filters[type as keyof Filters].value =
									values.map((item: { name: string }) => item.name)[0] === "true";
							} else {
								this.filters[type as keyof Filters].value = values.map(
									(item: { name: string }) => item.name,
								)[0];
							}
						}
					}
				}
			}

			this.setFiltersQuery();
		},
		toggleView(view: "map" | "list") {
			this.view = view;
			useQueryUrlify("view", this.view);
		},
		applySorting(mode: "alphabetical" | "recent") {
			this.sortMode = mode;

			if (mode === "recent") {
				this.sorting = [
					{
						type: "post_date_timestamp",
						order: "desc",
					},
				];
			} else {
				this.sorting = [
					{
						type: "title",
						order: "asc",
					},
				];
			}
		},
		setSorting(mode: "alphabetical" | "recent") {
			this.applySorting(mode);
			useQueryUrlify("sortBy", mode);

			this.fetch(true);
		},
		setProjectBuilding() {
			this.filters.type.values = [];

			if (this.filters.building.value) {
				this.filters.type.values.push("building");
			}

			if (this.filters.project.value) {
				this.filters.type.values.push("project");
			}
		},
	},
});
